'use client';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Alert } from '@//components/Alert';
import { OverflowGridItem } from '@//components/OverflowGrid';
import ProductCardContainer from '@//containers/ProductCardContainer';
import ProductCardSkeleton from '@//containers/ProductCardSkeleton';
import useRelatedVehicles from '@//data/search/vehicles/useRelatedVehicles';
import { useMarketMetadata } from '@//providers/MarketSettingsProvider';

const skeletonArray = new Array(3).fill(null);

interface RelatedVehiclesResultProps {
  id?: string;
}

// On the item not-found page the id cannot be passed as a prop (Next.js
// renders not-found boundaries without route params), so derive it from the
// item page URL as a fallback. The public path uses the market's `itemPage`
// segment (e.g. `/objekt/{id}`), not the internal `/item` route folder.
const RelatedVehiclesResult = ({ id }: RelatedVehiclesResultProps) => {
  const t = useTranslations('pages.item.relatedVehiclesSection');
  const pathname = usePathname();
  const marketMetadata = useMarketMetadata();
  const segments = pathname.split('/').filter(Boolean);
  const itemPageSegment = marketMetadata.pageLinks.itemPage.split('/').filter(Boolean).pop();
  const itemIndex = itemPageSegment ? segments.indexOf(itemPageSegment) : -1;
  const resolvedId = id ?? (itemIndex >= 0 ? segments[itemIndex + 1] : undefined);
  const relatedVehiclesQuery = useRelatedVehicles(resolvedId);
  const relatedVehicles = relatedVehiclesQuery.data;

  if (relatedVehiclesQuery.isLoading)
    return (
      <>
        {skeletonArray.map((_, index) => (
          <OverflowGridItem key={index}>
            <div role="status" aria-live="polite" className="sr-only">
              {t('loading')}
            </div>
            <ProductCardSkeleton key={index} />
          </OverflowGridItem>
        ))}
      </>
    );

  if (relatedVehiclesQuery.isError) return <Alert id="alert-error">{t('error')}</Alert>;

  if (!relatedVehicles?.length) return <Alert id="alert-no-vehicles">{t('noVehicles')}</Alert>;

  return (
    <>
      {relatedVehicles.map((vehicle, index: number) => (
        <OverflowGridItem key={vehicle._id}>
          <ProductCardContainer
            item={vehicle}
            index={index}
            itemPage={marketMetadata.pageLinks.itemPage}
            currency={marketMetadata.currency}
            testId={`related-vehicle-${index}`}
            imagePreset="overflowGrid"
            loading="lazy"
            headingLevel={3}
          />
        </OverflowGridItem>
      ))}
    </>
  );
};

export default RelatedVehiclesResult;
